<template lang="pug">
.residents-for-cooperation-system-table-component
  //- テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    //- ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    //- レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    //- フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    //- ヘッダー：選択
    template(#head(checked)="data")
      .d-flex.flex-column
        label(style="margin-left:6px;")
          input(v-model="_allSelect" type="checkbox")
          span.text
    //- ヘッダー：部屋名
    template(#head(roomName)="data")
      .d-flex.justify-content-center.align-items-center
        span.mr-2 部屋名
        span.required 必須
    //- ヘッダー：利用者グループ
    template(#head(groupName)="data")
      .d-flex.justify-content-center.align-items-center
        span.mr-2 利用者グループ
        span.required 必須
    //- セル：登録情報
    template(#cell(checked)="row")
      .d-flex.flex-column(v-if="row.item.errors.length < 1")
        label(style="margin-left:6px;")
          input(v-model="row.item.checked" @change="changeChecked(row.item)" type="checkbox")
          span.text
    //- セル：状況
    template(#cell(type)="row")
      span(:class="getTypeClass(row.item.type)") {{ row.item.type }}
    //- セル：部屋名
    template(#cell(roomName)="row")
      span(v-if="row.item.type == '削除'") {{ row.item.room }}
      .select-room(v-else)
        b-form-select(v-model="row.item.csRoomId" :name="'room-' + row.item.insuredNumber + '-' + row.item.careInsuranceInsuredNumber"
          :options="_roomOptions" :class="{ 'noinput': !row.item.csRoomId }" style="max-width:240px;")
        .error-messages(v-for="error in errors.collect('room-' + row.item.insuredNumber + '-' + row.item.careInsuranceInsuredNumber)")
          label.error-message {{ error }}
    //- セル：利用者グループ
    template(#cell(groupName)="row")
      span(v-if="row.item.type == '削除'") {{ row.item.residentGroupName }}
      .select-group(v-else)
        b-form-select(v-model="row.item.groupId" :name="'group-' + row.item.insuredNumber + '-' + row.item.careInsuranceInsuredNumber"
          :options="_groupOptions" :class="{ 'noinput': !row.item.groupId }" style="max-width:240px;"
          @change="changeGroupId(row.item)")
        .error-messages(v-for="error in errors.collect('group-' + row.item.insuredNumber + '-' + row.item.careInsuranceInsuredNumber)")
          label.error-message {{ error }}
    //- セル：登録情報
    template(#cell(detail)="row")
      button.btn.print-btn(@click="onDetail(row.item)") 詳細
    //- セル：備考
    template(#cell(errors)="row")
      .error-message(v-for="error in row.item.errors") {{ error }}
</template>

<script lang="ts">
import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { Validator } from "vee-validate";

import { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import { ResidentWithDiff } from "@/commons/services/Residents";

/**
 * 利用者テーブルコンポーネント
 */
@Component({})
export default class ResidentsForCooperationSystemTable extends Vue {
  // 親コンポーネントのバリデーターをInjectionする
  @Inject("$validator") $validator!: Validator;

  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<ResidentsForCooperationSystemTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<ResidentsForCooperationSystemTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** フィルター */
  @Prop()
  filter!: ResidentsForCooperationSystemTableFilterSetting;
  /** 部屋名選択肢 */
  @Prop({ default: [] })
  roomOptions!: Array<{ value: number; text: string }>;
  /** 利用者グループ選択肢 */
  @Prop({ default: [] })
  groupOptions!: Array<{ value: number; text: string }>;
  /** 利用者グループ選択肢 */
  @Prop()
  residentGroupMap!: Map<number, ResidentGroupsGetModel>;
  /** 全て選択列のチェック */
  @Prop()
  allSelect!: boolean;

  /**
   * ### テーブルの行情報を返す
   */
  get _value(): Array<ResidentsForCooperationSystemTableItem> {
    return this.value;
  }

  /**
   * ### 部屋選択肢を返す
   */
  get _roomOptions(): Array<{ value: number; text: string }> {
    return this.roomOptions;
  }

  /**
   * ### 利用者グループ選択肢を返す
   */
  get _groupOptions(): Array<{ value: number; text: string }> {
    return this.groupOptions;
  }

  /**
   * ### 列：選択のチェックボックス状態を返す
   */
  get _allSelect(): boolean {
    return this.allSelect;
  }

  /**
   * ### 列：選択のチェックボックス押下処理
   *
   * - 全て選択を更新し、表示しているページの全行を選択状態にする（親コンポーネントで実施）
   */
  set _allSelect(newValue: boolean) {
    this.$emit("update:allSelect", newValue);
    this.$emit("onAllSelect");
    return;
  }

  /**
   * ### フィルタリング
   */
  public filterTable(item: ResidentsForCooperationSystemTableItem, filter: ResidentsForCooperationSystemTableFilterSetting): boolean {
    return this.filterType(item, filter) && this.filterSearchWords(item, filter);
  }

  public filterType(item: ResidentsForCooperationSystemTableItem, filter: ResidentsForCooperationSystemTableFilterSetting): boolean {
    if (filter.type == "全て") {
      return true;
    }

    if (item.type == filter.type) {
      return true;
    } else {
      return false;
    }
  }

  public filterSearchWords(item: ResidentsForCooperationSystemTableItem, filter: ResidentsForCooperationSystemTableFilterSetting): boolean {
    if (filter.searchWord == "") {
      return true;
    }

    const words: string[] = this.$filterUtil.createSearchWord(filter.searchWord);

    let foundNeme: boolean = true;
    let foundKanaName: boolean = true;

    for (const word of words) {
      if (item.name.indexOf(word) == -1) {
        foundNeme = false;
      }

      // ふりがなが登録されている場合は文字列を含むかチェックする
      if (item.kanaName) {
        if (item.kanaName.indexOf(word) == -1) {
          foundKanaName = false;
        }
      }
      // ふりがなが登録されていない場合は対象外として扱う
      else {
        foundKanaName = false;
      }
    }

    return foundNeme || foundKanaName;
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }

  /**
   * ### 種別毎の文字色のCSSクラスを返却する
   */
  public getTypeClass(type: "新規" | "変更" | "削除"): string {
    switch (type) {
      case "新規": {
        return "key-color-message font-weight-bold";
      }
      case "変更": {
        return "font-weight-bold";
      }
      case "削除": {
        return "warning-message font-weight-bold";
      }
    }
  }

  /**
   * ### チェックボックスの変更イベント
   *
   * - 選択されると背景色を変更して選択したことが分かりやすくする
   */
  public changeChecked(item: ResidentsForCooperationSystemTableItem): void {
    if (item.checked == true) {
      item._rowVariant = "row-checked";
    } else {
      item._rowVariant = "";
    }
    this.$forceUpdate();
    return;
  }

  /**
   * ### 利用者グループの変更イベント
   *
   * - 利用者グループ名を更新する
   */
  changeGroupId(item: ResidentsForCooperationSystemTableItem): void {
    const group: ResidentGroupsGetModel | undefined = this.residentGroupMap.get(item.groupId as number);
    item.residentGroupName = group ? group.name : "";
    return;
  }

  /**
   * ### 詳細ボタン押下イベント
   */
  onDetail(item: ResidentsForCooperationSystemTableItem): void {
    this.$emit("onDetail", item);
    return;
  }
}

/** テーブルの列名の情報 */
export interface ResidentsForCooperationSystemTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface ResidentsForCooperationSystemTableItem extends ResidentWithDiff {
  /** 反映対象のチェック */
  checked: boolean;
  /** 新規・変更・削除の種別 */
  type: "新規" | "変更" | "削除";

  /** エラー内容 */
  errors: string[];

  /** 色情報 */
  _rowVariant: string | null;
}

export interface ResidentsForCooperationSystemTableFilterSetting {
  type: "全て" | "新規" | "変更" | "削除";
  searchWord: string;
}
</script>

<style lang="scss" scoped>
.residents-for-cooperation-system-table-component {
  .detail-btn {
    width: 90px;
  }
}
</style>
